import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DoctorPatientInfo from '../components/DoctorPatientInfo';
import CallTypeBadge from '../components/CallTypeBadge';
import DoctorNavbar from '../components/DoctorNavbar';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';
import { clearDoctorSessionLocal, setDoctorSession, markAuthBounce, getStoredDoctorId, isDoctorAuthenticated } from '../utils/auth';
import { useDoctorLiveRefresh } from '../hooks/useDoctorLiveRefresh';

const SLOT_DURATION_OPTIONS = [
  { value: '15', label: '15 Minutes' },
  { value: '30', label: '30 Minutes' },
  { value: '45', label: '45 Minutes' },
  { value: '60', label: '60 Minutes' },
];

function DoctorDashboard() {
  /* ---------------- STATE ---------------- */
  const navigate = useNavigate();
  const storedId = getStoredDoctorId();
  const hasLocalSession = isDoctorAuthenticated() && !!storedId;

  const [doctorId, setDoctorId] = useState(hasLocalSession ? (Number(storedId) || storedId) : null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Skip full-screen wait when we already have a session from login
  const [authLoading, setAuthLoading] = useState(!hasLocalSession);
  const [dataLoading, setDataLoading] = useState(!!hasLocalSession);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showEditSlotModal, setShowEditSlotModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [appointmentToReject, setAppointmentToReject] = useState(null);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotForm, setSlotForm] = useState({
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    duration: '30',
    days: [],
    call_type: 'VIDEO',
  });
  const [editSlotForm, setEditSlotForm] = useState({
    date: '',
    start_time: '',
    end_time: '',
    duration: '30',
    call_type: 'VIDEO',
  });

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    if (hasLocalSession) {
      const id = Number(storedId) || storedId;
      setDoctorId(id);
      loadDashboardData(id);
      // Verify session in background — don't block UI
      api.get('doctor/session-check/')
        .then((res) => {
          if (res.data.authenticated) {
            setDoctorSession(res.data.doctor_id);
            setDoctorId(res.data.doctor_id);
          } else {
            markAuthBounce();
            clearDoctorSessionLocal();
            navigate('/doctor/login', { replace: true });
          }
        })
        .catch(() => {
          markAuthBounce();
          clearDoctorSessionLocal();
          navigate('/doctor/login', { replace: true });
        });
      return;
    }
    checkSession();
  }, []);

  useDoctorLiveRefresh(doctorId, (id) => {
    loadDashboardData(id, false);
  });

  const checkSession = async () => {
    try {
      const res = await api.get('doctor/session-check/');
      if (res.data.authenticated) {
        setDoctorSession(res.data.doctor_id);
        setDoctorId(res.data.doctor_id);
        setAuthLoading(false);
        loadDashboardData(res.data.doctor_id);
        return;
      }
    } catch (err) {
      console.error('Doctor Session check failed:', err);
    }

    setAuthLoading(false);
    markAuthBounce();
    clearDoctorSessionLocal();
    navigate('/doctor/login', { replace: true });
  };

  const loadDashboardData = async (doctorId, showLoading = true) => {
    try {
      if (showLoading) setDataLoading(true);

      const [profileRes, appointmentsRes, slotsRes] = await Promise.all([
        api.get(`doctor/profile/${doctorId}/`),
        api.get('appointments/doctor/my-appointments/'),
        api.get('appointments/doctor/slots/all/')
      ]);

      setDoctorInfo(profileRes.data);
      setAppointments(appointmentsRes.data);
      setSlots(slotsRes.data);

      // Fetch notifications separately (don't block page load)
      fetchNotifications();
    } catch (err) {
      console.error("Error loading doctor dashboard data:", err);
      // Interceptor handles 401 redirect; avoid double bounce
      if (err.response?.status === 401 && showLoading) {
        markAuthBounce();
        clearDoctorSessionLocal();
        navigate('/doctor/login', { replace: true });
      }
    } finally {
      if (showLoading) setDataLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('notification/doctor/?limit=5');
      console.log("Doctor notifications response:", res.data);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Error fetching doctor notifications:", err);
      // Set empty notifications on error
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`notification/doctor/mark-read/${notificationId}/`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('notification/doctor/mark-all-read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'APPOINTMENT_BOOKED':
        return 'bi-calendar-plus-fill text-white';
      case 'APPOINTMENT_CANCELLED':
        return 'bi-calendar-x-fill text-danger';
      case 'APPOINTMENT_RESCHEDULED':
        return 'bi-calendar-event text-warning';
      case 'PAYMENT_SUCCESS':
        return 'bi-credit-card-fill text-success';
      default:
        return 'bi-bell-fill text-white';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const updateBookingStatus = async (appointmentId, action, reason = "") => {
    try {
      await api.post('appointments/doctor/booking/update/', {
        appointment_id: appointmentId,
        action: action,
        reason: reason
      });
      // Refresh appointments
      const appointmentsRes = await api.get('appointments/doctor/my-appointments/');
      setAppointments(appointmentsRes.data);
      toast.success(`Booking ${action}ed successfully!`, {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update booking status", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const toggleSlotStatus = async (slotId) => {
    try {
      await api.post(`appointments/doctor/slots/toggle-status/${slotId}/`);
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update slot status", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteSlotClick = (slotId) => {
    setSlotToDelete(slotId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteSlotConfirm = async () => {
    if (!slotToDelete) return;

    try {
      await api.delete(`appointments/doctor/slots/delete/${slotToDelete}/`);
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
      toast.success("Slot deleted successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete slot. Note: Booked slots cannot be deleted.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setShowDeleteConfirm(false);
      setSlotToDelete(null);
    }
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();

    // Validate days selection
    if (slotForm.days.length === 0) {
      toast.warning("Please select at least one day", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const response = await api.post('appointments/doctor/slots/create/', slotForm);
      setShowSlotModal(false);
      setSlotForm({ start_date: '', end_date: '', start_time: '', end_time: '', duration: '30', days: [], call_type: 'VIDEO' });
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
      toast.success(response.data?.msg || "Slots created successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
      if (response.data?.warning) {
        toast.warning(response.data.warning, {
          position: "bottom-right",
          autoClose: 5000,
        });
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.response?.data?.msg || "Failed to create slots";
      toast.error(errMsg, {
        position: "bottom-right",
        autoClose: err.response?.status === 409 ? 6000 : 3000,
      });
    }
  };


  const toggleDay = (day) => {
    setSlotForm(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  const handleEditSlot = (slot) => {
    setSelectedSlot(slot);
    // Format time from "HH:MM:SS" to "HH:MM" for time input
    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      // If already in HH:MM format, return as is
      if (timeStr.length === 5) return timeStr;
      // If in HH:MM:SS format, extract HH:MM
      if (timeStr.length >= 5) return timeStr.substring(0, 5);
      return timeStr;
    };

    setEditSlotForm({
      date: slot.date || slot.date_from || '',
      start_time: formatTime(slot.start_time || ''),
      end_time: formatTime(slot.end_time || ''),
      duration: slot.duration || '30',
      call_type: slot.call_type || 'VIDEO',
    });
    setShowEditSlotModal(true);
  };

  const handleUpdateSlot = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;

    try {
      await api.put(`appointments/doctor/slots/update/${selectedSlot.id}/`, editSlotForm);
      setShowEditSlotModal(false);
      setSelectedSlot(null);
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
      toast.success("Slot updated successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update slot", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
  };

  const handleRescheduleAppointment = async (newSlotId) => {
    if (!selectedAppointment || !newSlotId) return;

    try {
      await api.post('appointments/doctor/reschedule/', {
        appointment_id: selectedAppointment.appointment_id,
        new_slot_id: newSlotId
      });
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      const appointmentsRes = await api.get('appointments/doctor/my-appointments/');
      setAppointments(appointmentsRes.data);
      const slotsRes = await api.get('appointments/doctor/slots/all/');
      setSlots(slotsRes.data);
      toast.success("Appointment rescheduled successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reschedule appointment", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-soft min-vh-100">
      <DoctorNavbar />

      <div className="container py-4">

        {/* HEADER SECTION */}
        <div className="row mb-4 align-items-center">
          <div className="col">
            <h2 className="fw-bold text-dark mb-0">
              Hello, {doctorInfo?.name || (dataLoading ? '...' : 'Doctor')}!
            </h2>
            <p className="text-muted">Manage your appointments and availability here.</p>
          </div>
        </div>

        {dataLoading && (
          <div className="text-center mb-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        )}

        <div className="row g-4">

          {/* STATS SECTION */}
          <div className="col-12">
            <div className="row g-3">
              <div className="col-6 col-md-4 col-lg-3">
                <div
                  className="card border-0 shadow-hover rounded-4 text-center p-3 bg-soft-primary h-100 d-flex flex-column"
                  style={{ minHeight: '160px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onClick={() => navigate('/doctor/appointments', { state: { activeTab: 'pending' } })}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3 shadow-sm" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-clock-history fs-4 text-primary"></i>
                  </div>
                  <h3 className="fw-bold text-primary mb-2">{appointments.filter(a => a.status === 'PENDING').length}</h3>
                  <small className="text-muted text-uppercase fw-bold">Pending Requests</small>
                  <div className="mt-auto"></div>
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-3">
                <div
                  className="card border-0 shadow-hover rounded-4 text-center p-3 bg-success bg-opacity-10 h-100 d-flex flex-column"
                  style={{ minHeight: '160px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onClick={() => navigate('/doctor/appointments', { state: { activeTab: 'upcoming' } })}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-calendar-check fs-4 text-success"></i>
                  </div>
                  <h3 className="fw-bold text-success mb-2">{appointments.filter(a => a.booking_type === 'UPCOMING' && a.status === 'ACCEPTED').length}</h3>
                  <small className="text-muted text-uppercase fw-bold">Upcoming Sessions</small>
                  <div className="mt-auto"></div>
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-3">
                <div
                  className="card border-0 shadow-hover rounded-4 text-center p-3 bg-info bg-opacity-10 h-100 d-flex flex-column"
                  style={{ minHeight: '160px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onClick={() => navigate('/doctor/appointments', { state: { activeTab: 'slots' } })}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-calendar3 fs-4 text-info"></i>
                  </div>
                  <h3 className="fw-bold text-info mb-2">{slots.length}</h3>
                  <small className="text-muted text-uppercase fw-bold">Total Slots</small>
                  <p className="small text-muted mb-0 mt-2">
                    {slots.filter(s => s.is_booked).length} booked, {slots.filter(s => !s.is_booked && s.is_active).length} available
                  </p>
                  <div className="mt-auto"></div>
                </div>
              </div>

              <div className="col-6 col-md-4 col-lg-3">
                <div
                  className="card border-0 shadow-hover rounded-4 text-center p-3 bg-warning bg-opacity-10 h-100 d-flex flex-column"
                  style={{ minHeight: '160px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onClick={() => navigate('/doctor/profile')}
                >
                  <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-2 mx-auto" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-award fs-4 text-warning"></i>
                  </div>
                  <h6 className="fw-bold text-warning mb-1 text-truncate px-2">{doctorInfo?.specialization || 'N/A'}</h6>
                  <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>Specialization</small>
                  {doctorInfo?.experience && (
                    <p className="small text-muted mb-0 mt-1">{doctorInfo.experience} Years Exp.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* NOTIFICATIONS SECTION */}
          <div className="col-12">
            <div className="card border-0 shadow-hover rounded-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-bell me-2 text-primary"></i>Recent Notifications
                    {unreadCount > 0 && (
                      <span className="badge bg-danger rounded-pill ms-2">{unreadCount}</span>
                    )}
                  </h5>
                  <div className="d-flex gap-2">
                    {unreadCount > 0 && (
                      <button
                        className="btn btn-sm btn-outline-primary rounded-pill"
                        onClick={markAllNotificationsRead}
                      >
                        <i className="bi bi-check-all me-1"></i>Mark all read
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-primary rounded-pill"
                      onClick={() => navigate('/doctor/notifications')}
                    >
                      <i className="bi bi-arrow-right me-1"></i>View All
                    </button>
                  </div>
                </div>

                {notifications.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bi bi-bell-slash fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted mb-0">No notifications yet</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    {notifications.slice(0, 4).map((notification) => (
                      <div key={notification.id} className="col-md-6 col-lg-3">
                        <div
                          className={`card border-0 h-100 ${!notification.is_read ? 'bg-light' : 'bg-white'}`}
                          style={{ cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                          onClick={() => {
                            if (!notification.is_read) {
                              markNotificationRead(notification.id);
                            }
                          }}
                        >
                          <div className="card-body p-3">
                            <div className="d-flex align-items-start">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2">
                                <i className={`bi ${getNotificationIcon(notification.notification_type)}`}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                  <h6 className={`mb-1 small ${!notification.is_read ? 'fw-bold' : ''}`}>
                                    {notification.title}
                                  </h6>
                                  {!notification.is_read && (
                                    <span className="badge bg-primary rounded-circle" style={{ width: '8px', height: '8px', padding: 0 }}></span>
                                  )}
                                </div>
                                <p className="text-muted mb-1 small" style={{ fontSize: '0.8rem' }}>
                                  {notification.message.length > 50
                                    ? notification.message.substring(0, 50) + '...'
                                    : notification.message}
                                </p>
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                  {formatTimeAgo(notification.created_at)}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* APPOINTMENT REQUESTS */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-hover rounded-4 h-100">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0 d-flex align-items-center">
                    <i className="bi bi-calendar-event me-2 text-primary"></i>
                    Booking Requests
                  </h5>
                  <button
                    className="btn btn-sm btn-primary rounded-pill"
                    onClick={() => navigate('/doctor/appointments')}
                  >
                    <i className="bi bi-arrow-right me-1"></i>View All
                  </button>
                </div>

                {appointments.filter(a => a.status === 'PENDING').length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No new booking requests at the moment.</p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {appointments.filter(a => a.status === 'PENDING').map(a => (
                      <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom bg-light bg-opacity-50 rounded-3 mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                <i className="bi bi-person-fill text-white"></i>
                              </div>
                              <div>
                                <DoctorPatientInfo appointment={a} />
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              <span className="badge bg-white bg-opacity-10 text-primary border border-primary rounded-pill px-3">
                                <i className="bi bi-calendar-event me-1"></i> {a.date}
                              </span>
                              <span className="badge bg-info bg-opacity-10 text-info border border-info rounded-pill px-3">
                                <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                              </span>
                              <CallTypeBadge callType={a.call_type} />
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-2 ms-3">
                            <button
                              className="btn btn-sm btn-success rounded-pill px-3"
                              onClick={() => updateBookingStatus(a.appointment_id, 'accept')}
                            >
                              <i className="bi bi-check-circle me-1"></i>Accept
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger rounded-pill px-3"
                              onClick={() => {
                                setAppointmentToReject(a.appointment_id);
                                setRejectReason('');
                                setShowRejectModal(true);
                              }}
                            >
                              <i className="bi bi-x-circle me-1"></i>Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* RECENTLY ACCEPTED */}
                <div className="d-flex justify-content-between align-items-center mt-5 mb-4">
                  <h5 className="fw-bold mb-0 d-flex align-items-center">
                    <i className="bi bi-check2-circle me-2 text-success"></i>
                    Upcoming Appointments
                  </h5>
                  <button
                    className="btn btn-sm btn-primary rounded-pill"
                    onClick={() => navigate('/doctor/appointments')}
                  >
                    <i className="bi bi-arrow-right me-1"></i>View All
                  </button>
                </div>
                {appointments.filter(a => a.status === 'ACCEPTED' && a.booking_type === 'UPCOMING').length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bi bi-calendar-x fs-1 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No upcoming appointments scheduled.</p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {appointments
                      .filter(a => a.status === 'ACCEPTED' && a.booking_type === 'UPCOMING')
                      .sort((a, b) => {
                        const dateA = new Date(`${a.date} ${a.start_time}`);
                        const dateB = new Date(`${b.date} ${b.start_time}`);
                        return dateA - dateB;
                      })
                      .map(a => (
                        <div key={a.appointment_id} className="list-group-item px-0 py-3 border-0 border-bottom">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <DoctorPatientInfo appointment={a} titleClass="fw-bold mb-1" subtitleClass="text-muted small mb-2" />
                              <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-success bg-opacity-10 text-success border border-success rounded-pill px-3">
                                  <i className="bi bi-calendar-check me-1"></i> {a.date}
                                </span>
                                <span className="badge bg-white bg-opacity-10 text-primary border border-primary rounded-pill px-3">
                                  <i className="bi bi-clock me-1"></i> {a.start_time} - {a.end_time}
                                </span>
                                <CallTypeBadge callType={a.call_type} />
                              </div>
                            </div>
                            <div className="d-flex flex-column gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                onClick={() => {
                                  setSelectedAppointment(a);
                                  setShowDetailModal(true);
                                }}
                              >
                                <i className="bi bi-eye me-1"></i>View Details
                              </button>
                              <button
                                className="btn btn-sm btn-outline-warning rounded-pill px-3"
                                onClick={() => handleReschedule(a)}
                              >
                                <i className="bi bi-calendar-event me-1"></i>Reschedule
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AVAILABILITY SLOTS */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-hover rounded-4 h-100">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">
                    <i className="bi bi-clock-history me-2 text-primary"></i>
                    Manage Slots
                  </h5>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-primary rounded-pill" onClick={() => setShowSlotModal(true)}>
                      <i className="bi bi-plus-lg me-1"></i>New Slot
                    </button>
                    {slots.length > 6 && (
                      <button
                        className="btn btn-sm btn-outline-primary rounded-pill"
                        onClick={() => navigate('/doctor/appointments')}
                      >
                        View All
                      </button>
                    )}
                  </div>
                </div>

                <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: '600px' }}>
                  {slots.length === 0 ? (
                    <p className="text-muted text-center py-4">No slots created yet.</p>
                  ) : (
                    [...slots].sort((a, b) => {
                      const dateA = a.date || a.date_from || '';
                      const dateB = b.date || b.date_from || '';

                      // Handle invalid dates - put them at the bottom
                      if (!dateA || dateA === 'N/A') return 1;
                      if (!dateB || dateB === 'N/A') return -1;

                      // Sort by date descending (newest first)
                      const dateAObj = new Date(dateA);
                      const dateBObj = new Date(dateB);

                      // If same date, sort by start_time (earlier time first)
                      if (dateAObj.getTime() === dateBObj.getTime()) {
                        const timeA = a.start_time || '';
                        const timeB = b.start_time || '';
                        return timeA.localeCompare(timeB);
                      }

                      return dateBObj.getTime() - dateAObj.getTime();
                    }).slice(0, 6).map(s => {
                      const slotDate = s.date || s.date_from || 'N/A';
                      const slotDay = s.day_of_week || '';
                      const isPast = slotDate && new Date(slotDate) < new Date().setHours(0, 0, 0, 0);

                      return (
                        <div key={s.id} className={`list-group-item px-0 py-3 border-0 border-bottom ${isPast ? 'opacity-50' : ''}`}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <p className="fw-bold mb-0 small">{slotDate}</p>
                                {slotDay && <span className="badge bg-light text-dark small">{slotDay}</span>}
                                <CallTypeBadge callType={s.call_type} />
                              </div>
                              <p className="text-muted mb-0 small">
                                <i className="bi bi-clock me-1"></i>
                                {s.start_time} - {s.end_time}
                                {s.duration && <span className="ms-2 text-info">({s.duration} min)</span>}
                              </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {s.is_booked ? (
                                <span className="badge bg-success rounded-pill px-3">
                                  <i className="bi bi-check-circle me-1"></i>Booked
                                </span>
                              ) : (
                                <>
                                  <div className="form-check form-switch me-2">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={s.is_active}
                                      onChange={() => toggleSlotStatus(s.id)}
                                      title={s.is_active ? "Active - Click to deactivate" : "Inactive - Click to activate"}
                                    />
                                  </div>
                                  <button
                                    className="btn btn-sm btn-outline-primary border-0"
                                    onClick={() => handleEditSlot(s)}
                                    title="Edit slot"
                                    disabled={isPast}
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger border-0"
                                    onClick={() => handleDeleteSlotClick(s.id)}
                                    title="Delete slot"
                                    disabled={isPast}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* CREATE SLOT MODAL */}
      {showSlotModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Create Availability Slots</h5>
                <button type="button" className="btn-close" onClick={() => setShowSlotModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleCreateSlot}>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        value={slotForm.start_date}
                        onChange={(e) => setSlotForm({ ...slotForm, start_date: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        min={slotForm.start_date || new Date().toISOString().split('T')[0]}
                        value={slotForm.end_date}
                        onChange={(e) => setSlotForm({ ...slotForm, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold">Start Time</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={slotForm.start_time}
                        onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold">End Time</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={slotForm.end_time}
                        onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Call Type</label>
                    <div className="d-flex gap-2">
                      {['VIDEO', 'AUDIO'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          className={`btn flex-fill rounded-pill ${slotForm.call_type === type ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setSlotForm({ ...slotForm, call_type: type })}
                        >
                          <i className={`bi ${type === 'VIDEO' ? 'bi-camera-video' : 'bi-telephone'} me-1`}></i>
                          {type === 'VIDEO' ? 'Video Call' : 'Audio Call'}
                        </button>
                      ))}
                    </div>
                    <small className="text-muted">Same time cannot be used for both audio and video.</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Slot Duration (Minutes)</label>
                    <FilterSelect
                      value={String(slotForm.duration)}
                      onChange={(duration) => setSlotForm({ ...slotForm, duration })}
                      options={SLOT_DURATION_OPTIONS}
                      placeholder="Slot Duration"
                      aria-label="Slot Duration"
                      variant="bordered"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="form-label small fw-bold mb-2">Select Days</label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <button
                          key={day}
                          type="button"
                          className={`btn rounded-pill px-3 ${slotForm.days.includes(day)
                            ? 'btn-primary'
                            : 'btn-outline-primary'
                            }`}
                          onClick={() => toggleDay(day)}
                        >
                          {day.substring(0, 3)}
                        </button>
                      ))}
                    </div>
                    {slotForm.days.length > 0 && (
                      <p className="small text-muted mt-2 mb-0">
                        Selected: {slotForm.days.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="d-grid">
                    <button type="submit" className="btn btn-primary btn-lg rounded-3 fw-bold">
                      <i className="bi bi-calendar-plus me-2"></i>Generate Slots
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPOINTMENT DETAIL MODAL */}
      {showDetailModal && selectedAppointment && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">Appointment Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4 text-center">
                  <div className="bg-primary bg-opacity-10 text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-person-fill text-white fs-2"></i>
                  </div>
                  <DoctorPatientInfo
                    appointment={selectedAppointment}
                    titleClass="fw-bold mb-0"
                    subtitleClass="text-muted small"
                  />
                </div>

                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Date</label>
                    <p className="fw-medium mb-0"><i className="bi bi-calendar-event me-2 text-primary"></i>{selectedAppointment.date}</p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Time</label>
                    <p className="fw-medium mb-0"><i className="bi bi-clock me-2 text-primary"></i>{selectedAppointment.start_time}</p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase d-block">Call Type</label>
                    <CallTypeBadge callType={selectedAppointment.call_type} />
                  </div>
                  <div className="col-12 border-top pt-3">
                    <label className="text-muted small fw-bold text-uppercase d-block">Status</label>
                    <span className={`badge rounded-pill ${selectedAppointment.status === 'ACCEPTED' ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {selectedAppointment.status}
                    </span>
                  </div>
                </div>

                <div className="mt-4 d-grid">
                  <button className="btn btn-primary rounded-3 fw-bold" onClick={() => setShowDetailModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT SLOT MODAL */}
      {showEditSlotModal && selectedSlot && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Edit Slot</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowEditSlotModal(false);
                  setSelectedSlot(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleUpdateSlot}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      value={editSlotForm.date}
                      onChange={(e) => setEditSlotForm({ ...editSlotForm, date: e.target.value })}
                    />
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold">Start Time</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={editSlotForm.start_time}
                        onChange={(e) => setEditSlotForm({ ...editSlotForm, start_time: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold">End Time</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={editSlotForm.end_time}
                        onChange={(e) => setEditSlotForm({ ...editSlotForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Call Type</label>
                    <div className="d-flex gap-2">
                      {['VIDEO', 'AUDIO'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          className={`btn flex-fill rounded-pill ${editSlotForm.call_type === type ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setEditSlotForm({ ...editSlotForm, call_type: type })}
                        >
                          <i className={`bi ${type === 'VIDEO' ? 'bi-camera-video' : 'bi-telephone'} me-1`}></i>
                          {type === 'VIDEO' ? 'Video' : 'Audio'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="form-label small fw-bold">Slot Duration (Minutes)</label>
                    <FilterSelect
                      value={String(editSlotForm.duration)}
                      onChange={(duration) => setEditSlotForm({ ...editSlotForm, duration })}
                      options={SLOT_DURATION_OPTIONS}
                      placeholder="Slot Duration"
                      aria-label="Slot Duration"
                      variant="bordered"
                    />
                  </div>
                  <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary btn-lg rounded-3 fw-bold">
                      <i className="bi bi-check-circle me-2"></i>Update Slot
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-3"
                      onClick={() => {
                        setShowEditSlotModal(false);
                        setSelectedSlot(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RESCHEDULE MODAL */}
      {showRescheduleModal && selectedAppointment && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-warning">Reschedule Appointment</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowRescheduleModal(false);
                  setSelectedAppointment(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Current Appointment:</h6>
                  <p className="text-muted mb-1">
                    <strong>Patient:</strong> {selectedAppointment.patient_name}
                    {selectedAppointment.booked_by_display && (
                      <span className="text-muted"> ({selectedAppointment.booked_by_display})</span>
                    )}
                  </p>
                  <p className="text-muted mb-1">
                    <strong>Date:</strong> {selectedAppointment.date}
                  </p>
                  <p className="text-muted mb-1">
                    <strong>Time:</strong> {selectedAppointment.start_time} - {selectedAppointment.end_time}
                  </p>
                  <p className="text-muted mb-0 d-flex align-items-center gap-2">
                    <strong>Call:</strong> <CallTypeBadge callType={selectedAppointment.call_type} />
                  </p>
                </div>

                <div className="border-top pt-4">
                  <h6 className="fw-bold mb-3">Select New Slot:</h6>
                  <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {slots
                      .filter(s => !s.is_booked && s.is_active)
                      .filter(s => {
                        if (selectedAppointment?.call_type && s.call_type && s.call_type !== selectedAppointment.call_type) {
                          return false;
                        }
                        const slotDateTime = new Date(`${s.date || s.date_from} ${s.start_time}`);
                        return slotDateTime >= new Date();
                      })
                      .sort((a, b) => {
                        const dateA = new Date(`${a.date || a.date_from} ${a.start_time}`);
                        const dateB = new Date(`${b.date || b.date_from} ${b.start_time}`);
                        return dateA - dateB;
                      })
                      .map(slot => (
                        <button
                          key={slot.id}
                          type="button"
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          onClick={() => handleRescheduleAppointment(slot.id)}
                        >
                          <div>
                            <p className="mb-0 fw-bold">
                              {slot.date || slot.date_from}
                              <span className={`badge ms-2 small ${slot.call_type === 'AUDIO' ? 'bg-info' : 'bg-primary'}`}>
                                {slot.call_type === 'AUDIO' ? 'Audio' : 'Video'}
                              </span>
                            </p>
                            <small className="text-muted">
                              {slot.start_time} - {slot.end_time} ({slot.duration} min)
                            </small>
                          </div>
                          <i className="bi bi-chevron-right text-primary"></i>
                        </button>
                      ))}
                    {slots.filter(s => {
                      if (s.is_booked || !s.is_active) return false;
                      if (selectedAppointment?.call_type && s.call_type && s.call_type !== selectedAppointment.call_type) {
                        return false;
                      }
                      return new Date(`${s.date || s.date_from} ${s.start_time}`) >= new Date();
                    }).length === 0 && (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-calendar-x fs-1 d-block mb-2"></i>
                        <p className="mb-0">No available slots for rescheduling</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE SLOT CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowDeleteConfirm(false);
                  setSlotToDelete(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-0">
                  Are you sure you want to delete this slot?
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSlotToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={handleDeleteSlotConfirm}
                >
                  <i className="bi bi-trash me-2"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT APPOINTMENT MODAL */}
      {showRejectModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-x-circle me-2"></i>Reject Appointment
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowRejectModal(false);
                  setAppointmentToReject(null);
                  setRejectReason('');
                }}></button>
              </div>
              <div className="modal-body p-4">
                <label className="form-label fw-bold">Rejection Reason (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowRejectModal(false);
                    setAppointmentToReject(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={() => {
                    if (appointmentToReject) {
                      updateBookingStatus(appointmentToReject, 'reject', rejectReason || "");
                      setShowRejectModal(false);
                      setAppointmentToReject(null);
                      setRejectReason('');
                    }
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE SLOT CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowDeleteConfirm(false);
                  setSlotToDelete(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-0">
                  Are you sure you want to delete this slot?
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSlotToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={handleDeleteSlotConfirm}
                >
                  <i className="bi bi-trash me-2"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT APPOINTMENT MODAL */}
      {showRejectModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-x-circle me-2"></i>Reject Appointment
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowRejectModal(false);
                  setAppointmentToReject(null);
                  setRejectReason('');
                }}></button>
              </div>
              <div className="modal-body p-4">
                <label className="form-label fw-bold">Rejection Reason (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowRejectModal(false);
                    setAppointmentToReject(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={() => {
                    if (appointmentToReject) {
                      updateBookingStatus(appointmentToReject, 'reject', rejectReason || "");
                      setShowRejectModal(false);
                      setAppointmentToReject(null);
                      setRejectReason('');
                    }
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DoctorDashboard;
